"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { HeroPointOverlay } from "@/lib/demo-finetunes";

interface PointOverlayProps {
  overlay: HeroPointOverlay;
}

export function PointOverlay({ overlay }: PointOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const img = el.parentElement?.querySelector("img");
    if (!img || !img.naturalWidth) return;

    const containerRect = el.parentElement!.getBoundingClientRect();
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerW / containerH;

    let renderW: number;
    let renderH: number;
    if (imgAspect > containerAspect) {
      renderW = containerW;
      renderH = containerW / imgAspect;
    } else {
      renderH = containerH;
      renderW = containerH * imgAspect;
    }

    setSize({ width: renderW, height: renderH });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    const img = containerRef.current?.parentElement?.querySelector("img");
    if (img) {
      img.addEventListener("load", measure);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (img) img.removeEventListener("load", measure);
    };
  }, [measure]);

  if (!size.width || !size.height) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <svg
        width={size.width}
        height={size.height}
        viewBox={`0 0 ${size.width} ${size.height}`}
        className="pointer-events-none"
      >
        {overlay.points.map((point, i) => {
          const cx = point.x * size.width;
          const cy = point.y * size.height;
          const color = point.correct === false ? "#ef4444" : "#22c55e";

          return (
            <g key={`pt-${i}`}>
              {/* Outer glow */}
              <circle cx={cx} cy={cy} r={6} fill={color} opacity={0.25} />
              {/* Inner dot */}
              <circle cx={cx} cy={cy} r={3.5} fill={color} />
              {/* Bright center */}
              <circle cx={cx} cy={cy} r={1.5} fill="white" opacity={0.7} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
